import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import { intToHex, parseHexColor } from 'js/src/libs/color-utils';
import { saveRawLuminary } from 'js/src/luminary';

export const setBackgroundSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The asset name (e.g. color_1)'),
    color: z
        .string()
        .regex(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
        .describe('The color as a hex code.'),
});
export type SetBackgroundArgs = z.infer<typeof setBackgroundSchema>;

export class SetBackgroundTool extends VideoStateTool<typeof setBackgroundSchema> {
    protected override readonly schema = setBackgroundSchema;
    protected override readonly toolName = TOOL_NAMES.setBackground;
    protected override readonly toolDescription =
        'Set background color for a specific scene and asset';

    public override getToolDisplayName(): string {
        return 'Setting background';
    }

    public get statusIcon(): string {
        return 'wallpaper';
    }

    public async execute(args: SetBackgroundArgs, context: ToolExecutionContext) {
        const { asset_name, color } = args;
        const { parentActionGroup } = context;

        const colorInt = parseHexColor(color);
        if (colorInt === null) {
            return 'Error: color must be a hex string like "#ff0000"';
        }

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];

            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }

            // Check if asset is a ColorAsset
            if (!VideoData.isColorAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a color asset`;
            }

            try {
                asset.sourceColor = colorInt;

                // Save changes to enable undo/redo functionality
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            } catch (error) {
                return `Error: Failed to set color for asset ${asset_name} in scene ${sceneShortId}`;
            }
            return `Success: Set background color for scene ${sceneShortId}, asset ${asset_name} to ${intToHex(colorInt)}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting background color: ${errorMessage}`;
        }
    }
}
